// react
import React, { useState, useEffect } from "react";

// react-router-dom
import { useNavigate, useLocation, Link } from "react-router-dom";

// context
import { UserAuth } from "context/AuthContext";

// react-pwa-install
import { useReactPWAInstall } from "react-pwa-install";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { useTheme, Container, Box, Grid, TextField, Button, Typography, Link as MuiLink } from "@mui/material";

// ui-components
import { LoginSnackbar, InstallBanner } from "ui-components/ORFeedbacks";
import Copyright from "ui-components/Copyright";

// firebase
import { requestPermission } from "config/firebase";

// SafeTwin
import { seedFlow } from "SafeTwin/3S/seedFlow";

// user-controller
import addFCMToken from "user-controller/addFCMToken";

// A ---------------------------------------------------------------------- M

const Signin = () => {
  const { user, setLanguage, setSeedFlow, signIn } = UserAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const { pwaInstall, supported, isInstalled } = useReactPWAInstall();

  const [showInstallBanner, setShowInstallBanner] = useState(supported() && !isInstalled());
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openError, setOpenError] = useState(false);

  const tag = location.state?.tag;

  useEffect(() => {
    setLanguage("it");
  }, []);

  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, []);

  const handleClick = () => {
    pwaInstall({
      title: t("install_pwa_title"),
      description: t("install_pwa_description"),
    })
      .then(() => console.log(t("installation_success")))
      .catch(() => console.log(t("installation_error")));
  };

  const handleSignIn = async () => {
    try {
      const userCredential = await signIn(email, password);

      // FCM Token
      const token = await requestPermission();
      if (token) await addFCMToken(userCredential.user.uid, token);

      // 3S Flow
      const isSeeded = await seedFlow(userCredential.user, password);
      setSeedFlow(isSeeded);

      // Signin Successful
      setOpenSuccess(true);
      console.log("Signin Successful");
    } catch (err) {
      console.error("Signin error:", err);
      setPassword("");
      setOpenError(true);
    }
  };

  const handleCloseSuccess = () => {
    setOpenSuccess(false);
    if (tag) navigate(`/${tag}`);
    else navigate("/");
  };

  const handleCloseError = () => {
    setOpenError(false);
  };

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}F2 0%, ${theme.palette.primary.main || theme.palette.primary.main}E6 50%, ${theme.palette.primary.main}D9 100%)`,
          backdropFilter: "blur(4px)",
          flexDirection: "column",
        }}
      >
        <Container
          component="main"
          maxWidth="sm"
          sx={{
            display: "flex",
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              p: 3,
              borderRadius: 4,
              bgcolor: "rgba(255,255,255,0.06)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(255,255,255,0.12)",
              boxShadow: "0 8px 30px rgba(0,0,0,0.5)",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 12px 36px rgba(0,0,0,0.6)",
              },
            }}
          >
            <Typography variant="h5" fontWeight={700} textAlign="center" sx={{ color: "rgba(255,255,255,0.9)", mb: 3, letterSpacing: "0.5px" }}>
              {t("sign_in")}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  variant="filled"
                  fullWidth
                  required
                  id="email"
                  label={t("email")}
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{
                    input: { color: "white" },
                    "& .MuiFilledInput-root": {
                      backgroundColor: "rgba(255,255,255,0.05)",
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255,255,255,0.6)",
                    },
                    "& input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px rgba(255,255,255,0.05) inset",
                      WebkitTextFillColor: "#ffffff",
                      transition: "background-color 5000s ease-in-out 0s",
                    },
                    "& input:-webkit-autofill:focus": {
                      WebkitBoxShadow: "0 0 0 1000px rgba(255,255,255,0.08) inset",
                      WebkitTextFillColor: "#ffffff",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  variant="filled"
                  fullWidth
                  required
                  id="password"
                  label={t("password")}
                  name="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{
                    input: { color: "white" },
                    "& .MuiFilledInput-root": {
                      backgroundColor: "rgba(255,255,255,0.05)",
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255,255,255,0.6)",
                    },
                    "& input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px rgba(255,255,255,0.05) inset",
                      WebkitTextFillColor: "#fff",
                      transition: "background-color 5000s ease-in-out 0s",
                    },
                    "& input:-webkit-autofill:focus": {
                      WebkitBoxShadow: "0 0 0 1000px rgba(255,255,255,0.08) inset",
                      WebkitTextFillColor: "#fff",
                    },
                  }}
                />
                <Typography align="right" mt={1}>
                  <MuiLink
                    component={Link}
                    to="/reset-password"
                    sx={{
                      textDecoration: "none",
                      color: "primary.main",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} sx={{ color: "rgba(255,255,255,0.8)" }}>
                      {t("reset_password")}
                    </Typography>
                  </MuiLink>
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSignIn}
                  sx={{
                    py: 1.5,
                    mt: 2,
                    borderRadius: 2,
                    fontWeight: 700,
                    letterSpacing: "0.5px",
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark || theme.palette.primary.main})`,
                    boxShadow: `0 4px 12px ${theme.palette.primary.main}66`,
                    transition: "all 0.25s ease",
                    "&:hover": {
                      background: `linear-gradient(90deg, ${theme.palette.primary.light || theme.palette.primary.main}, ${theme.palette.primary.main})`,
                      boxShadow: `0 6px 20px ${theme.palette.primary.main}99`,
                      transform: "translateY(-2px)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                      boxShadow: `0 3px 8px ${theme.palette.primary.main}66`,
                    },
                  }}
                >
                  {t("sign_in")}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Container>

        <Box sx={{ mt: 3, pb: 3 }}>
          <Copyright />
        </Box>
      </Box>

      {showInstallBanner && <InstallBanner showInstallBanner={showInstallBanner} handleCloseInstallBanner={() => setShowInstallBanner(false)} handleClick={handleClick} />}
      {openSuccess && <LoginSnackbar open={openSuccess} handleClose={handleCloseSuccess} severity="success" message="login_successful" autoHideDuration={3000} />}
      {openError && <LoginSnackbar open={openError} handleClose={handleCloseError} severity="error" message="email_password_wrong" autoHideDuration={3000} />}
    </>
  );
};

export default Signin;
